import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Token as TokenIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { 
  fetchCustomerCallsSinceReplenishment, 
  CustomerCallData,
  CustomerCallsResponse 
} from "../dataaccess/fetchCustomerCallsPublicAPI";
import { ModelUsageData } from "../dataaccess/fetchModelsPublicAPI";

interface CustomerCallsGridProps {
  customerId: string;
  projectId: string;
  jwtToken?: string;
  apiKey?: string; // Deprecated: Use jwtToken instead
  lastReplenishedAt?: string;
  title?: string;
  compact?: boolean;
  maxHeight?: number;
  initialPageSize?: number;
  className?: string;
  modelsData?: ModelUsageData[];
}

export const CustomerCallsGrid: React.FC<CustomerCallsGridProps> = ({
  customerId,
  projectId,
  jwtToken,
  apiKey, // Deprecated: backward compatibility
  lastReplenishedAt,
  title = "Recent API Calls",
  compact = false,
  maxHeight = 400,
  initialPageSize = 25,
  className,
  modelsData = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);

  // For now, allow rendering without authentication to prevent breaking existing usage
  // The actual data fetching will handle the authentication requirement
  const hasAuth = Boolean(jwtToken || apiKey);

  // Calculate offset for pagination
  const offset = page * rowsPerPage;

  // Debug: Log query enablement condition
  const queryEnabled = Boolean(customerId && hasAuth && (jwtToken || (apiKey && projectId)));
  console.log("CustomerCallsGrid query enabled:", {
    customerId: !!customerId,
    hasAuth,
    jwtToken: !!jwtToken,
    apiKey: !!apiKey,
    projectId: !!projectId,
    queryEnabled
  });

  const { data, isLoading, isError, error, refetch } = useQuery<CustomerCallsResponse>({
    queryKey: ["customer-calls", projectId, customerId, lastReplenishedAt, offset, rowsPerPage, jwtToken ? "jwt" : "apikey"],
    queryFn: () => {
      if (!jwtToken && !apiKey) {
        throw new Error('Either jwtToken or apiKey must be provided for data fetching');
      }
      return fetchCustomerCallsSinceReplenishment(
        projectId,
        customerId,
        apiKey,
        jwtToken,
        lastReplenishedAt || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // Default to 30 days ago
        offset,
        rowsPerPage
      );
    },
    staleTime: 60 * 1000, // 1 minute
    retry: 2,
    // Enable query if we have customerId and authentication (projectId not required for JWT)
    enabled: Boolean(customerId && hasAuth && (jwtToken || (apiKey && projectId))),
  });

  const calls = data?.data || [];
  const hasMore = data?.hasMore || false;

  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    if (compact) {
      return date.toLocaleDateString();
    }
    return date.toLocaleString();
  };

  // Format token counts for display
  const formatTokenCount = (count: string | number) => {
    const num = typeof count === 'string' ? parseInt(count) : count;
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Get status color for chip
  const getStatusColor = (status: string) => {
    const statusNum = parseInt(status);
    if (statusNum >= 200 && statusNum < 300) return 'success';
    if (statusNum >= 400 && statusNum < 500) return 'warning';
    if (statusNum >= 500) return 'error';
    return 'default';
  };

  // Get location display text
  const getLocationText = (call: CustomerCallData) => {
    const parts = [call.city, call.region, call.country].filter(Boolean);
    if (compact && parts.length > 2) {
      return `${call.city}, ${call.region}`;
    }
    return parts.join(', ');
  };

  // Format customer user info (avoid duplicates) - similar to CustomerBillingView
  const formatCustomerUserInfo = (call: CustomerCallData) => {
    const { customer_user_id, customer_user_name, customer_user_email, customerUserId, customerUserName, customerUserEmail } = call;
    
    // Combine the different field formats (some calls use customer_user_* and others use customerUser*)
    const userId = customer_user_id || customerUserId;
    const userName = customer_user_name || customerUserName;
    const userEmail = customer_user_email || customerUserEmail;
    
    if (!userId && !userName && !userEmail) {
      return null;
    }

    const parts: string[] = [];
    const seen = new Set<string>();

    // Add name if present and unique
    if (userName && !seen.has(userName.toLowerCase())) {
      parts.push(userName);
      seen.add(userName.toLowerCase());
    }

    // Add email if present and unique
    if (userEmail && !seen.has(userEmail.toLowerCase())) {
      parts.push(userEmail);
      seen.add(userEmail.toLowerCase());
    }

    // Add ID if present and unique
    if (userId && !seen.has(userId.toLowerCase())) {
      parts.push(userId);
      seen.add(userId.toLowerCase());
    }

    return parts.join(' • ');
  };

  // Get model name for display - uses modelsData to get accurate model names
  const getModelNameForCall = (call: CustomerCallData): string => {
    const modelId = call.modelId || '';
    
    // Try to find the model in modelsData to get the name
    if (modelsData.length > 0 && modelId) {
      const foundModel = modelsData.find(model => model.id === modelId);
      
      if (foundModel) {
        return foundModel.name;
      }
    }
    
    // Fallback to truncated model ID
    return modelId ? `${modelId.substring(0, 8)}...` : 'Unknown';
  };

  // Get model tier for display - uses modelsData for accurate categorization
  // Note: Only processes calls with modelId. Service calls (with serviceId) will be handled separately.
  const getModelTierForCall = (call: CustomerCallData): 'standard' | 'premium' | 'reasoning' | 'superPremium' => {
    const modelId = call.modelId || '';
    
    // Only process calls with modelId (skip serviceId calls for now)
    if (!modelId) {
      return 'standard'; // Default for non-model calls
    }
    
    // First try to find the model in the modelsData for accurate categorization
    if (modelsData.length > 0) {
      const foundModel = modelsData.find(model => model.id === modelId);
      
      if (foundModel) {
        // Map defaultServiceGroup to tier based on model data
        const serviceGroup = foundModel.defaultServiceGroup?.toLowerCase() || '';
        
        switch (serviceGroup) {
          case 'reasoning':
            return 'reasoning';
          case 'super premium':
          case 'superpremium':
            return 'superPremium';
          case 'premium':
            return 'premium';
          case 'standard':
          default:
            return 'standard';
        }
      } else {
        // Fallback to pattern matching when model not found in data
        console.log('Model not found in data, using fallback logic for:', { 
          callModelId: modelId
        });
      }
    }
    
    // Fallback to pattern matching for backwards compatibility when model not found in data
    const combined = modelId.toLowerCase();
    
    // Check for known reasoning model patterns
    if (combined.includes('o1') || combined.includes('reasoning')) {
      return 'reasoning';
    }
    
    // Check for known super premium model patterns
    if (combined.includes('claude-3-opus') || combined.includes('gpt-4o') || 
        combined.includes('super') || combined.includes('superpremium')) {
      return 'superPremium';
    }
    
    // Check for known premium model patterns
    if (combined.includes('premium') || 
        combined.includes('claude-3.5-sonnet') ||
        combined.includes('gpt-4') ||
        combined.includes('gemini-pro')) {
      return 'premium';
    }
    
    // Default to standard for basic models
    return 'standard';
  };

  // Handle page change
  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isLoading && calls.length === 0) {
    return (
      <Paper className={className} sx={{ p: compact ? 2 : 3, textAlign: "center" }}>
        <CircularProgress size={compact ? 24 : 32} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading API calls...
        </Typography>
      </Paper>
    );
  }

  if (isError) {
    return (
      <Paper className={className} sx={{ p: compact ? 2 : 3 }}>
        <Alert severity="error">
          <Typography variant={compact ? "body2" : "body1"}>
            Failed to load API calls
          </Typography>
          {!compact && (
            <Typography variant="caption" color="text.secondary">
              {error instanceof Error ? error.message : "Unknown error occurred"}
            </Typography>
          )}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper className={className} sx={{ p: compact ? 1 : 2, width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant={compact ? "subtitle1" : "h6"}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!compact && calls.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {calls.length} calls {hasMore ? '(more available)' : ''}
            </Typography>
          )}
          <Tooltip title="Refresh data">
            <IconButton 
              size="small" 
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {calls.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No API calls found
            {lastReplenishedAt && (
              <> since {formatTimestamp(lastReplenishedAt)}</>
            )}
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer sx={{ maxHeight }}>
            <Table stickyHeader size={compact ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Status</TableCell>
                  {!isMobile && !compact && <TableCell>Model</TableCell>}
                  <TableCell>Tier</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      
                      Tokens
                    </Box>
                  </TableCell>
                  {!compact && (
                    <TableCell>Location</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {calls.map((call) => {
                  const modelTier = getModelTierForCall(call);
                  const customerUserInfo = formatCustomerUserInfo(call);
                  
                  // Calculate number of columns for spanning
                  const colSpan = 5 + (!isMobile && !compact ? 1 : 0) + (!compact ? 1 : 0); // Time, Status, Model (conditional), Tier, Tokens, Location (conditional)
                  
                  return (
                    <React.Fragment key={call.id}>
                      {/* Main data row */}
                      <TableRow 
                        sx={{ 
                          '&:hover': { backgroundColor: 'action.hover' },
                          ...(customerUserInfo && !compact && { 
                            '& td': { 
                              borderBottom: 'none',
                              paddingBottom: '2px'
                            } 
                          })
                        }}
                      >
                        <TableCell>
                          <Typography variant={compact ? "caption" : "body2"}>
                            {formatTimestamp(call.createdAt)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Chip
                            label={call.status}
                            color={getStatusColor(call.status)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        
                        {!isMobile && !compact && (
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {getModelNameForCall(call)}
                            </Typography>
                          </TableCell>
                        )}
                        
                        <TableCell>
                          <Chip 
                            size="small"
                            label={modelTier}
                            color={
                              modelTier === 'reasoning' ? 'error' :
                              modelTier === 'superPremium' ? 'secondary' :
                              modelTier === 'premium' ? 'warning' : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {formatTokenCount(call.tokenCountPrompt)} + {formatTokenCount(call.tokenCountResponse)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              = {formatTokenCount(parseInt(call.tokenCountPrompt) + parseInt(call.tokenCountResponse))}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        {!compact && (
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {getLocationText(call)}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                      
                      {/* User details row - spans entire width */}
                      {customerUserInfo && !compact && (
                        <TableRow 
                          sx={{ 
                            '&:hover': { backgroundColor: 'action.hover' },
                            height: 'auto',
                            '& td': { 
                              border: 'none', 
                              padding: '0px 16px 4px 16px',
                              height: 'auto'
                            }
                          }}
                        >
                          <TableCell colSpan={colSpan} sx={{ 
                            paddingLeft: 4, 
                            border: 'none', 
                            padding: '0px 16px 4px 32px',
                            height: 'auto'
                          }}>
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              fontStyle: 'italic', 
                              lineHeight: 1,
                              display: 'block',
                              margin: 0
                            }}>
                              User: {customerUserInfo}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={hasMore ? (page + 1) * rowsPerPage + 1 : calls.length + page * rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelDisplayedRows={({ from, to, count }) => {
              if (hasMore) {
                return `${from}–${to} of many`;
              }
              return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
            }}
            showFirstButton
            showLastButton={!hasMore}
          />
        </>
      )}
    </Paper>
  );
};
